import { styled } from "@mui/material";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import Guid from "_shared/guids/guid";
import { createMRTColumnHelper, MaterialReactTable, useMaterialReactTable } from "material-react-table";
import React, { useCallback, useMemo } from "react";
import { useSession } from "users/__session/session-context";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import UserCoworkerQuery, {
  UserCoworkerFilterParameters,
  UserCoworkerOrderParameters,
  UserCoworkerOrderField,
} from "users/information/coworker-information/_api/commands/query-user-coworker-api-request";
import UserCoworker from "users/information/coworker-information/user-coworker";
import { useGetUserCoworkersQuery } from "users/information/coworker-information/_api/user-coworker-api";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";

const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));
const RowTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  fontWeight: "600",
  marginLeft: theme.spacing(1.5),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

type UserCoworkersProps = {
  selectedMemberUserIds?: Guid[];
  onSelected: (rowData: UserCoworker) => void;
};

export default function UserCoworkers(props: Readonly<UserCoworkersProps>) {
  const { selectedMemberUserIds, onSelected } = props;

  // Pagination state
  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);

  const session = useSession();

  const coworkerFilter = new UserCoworkerFilterParameters();
  const coworkerOrder = new UserCoworkerOrderParameters(UserCoworkerOrderField.Name, OrderDirection.Ascending);
  const coworkerPagination = PaginationParameters.create({ pageIndex, pageSize });
  const query = new UserCoworkerQuery(
    coworkerFilter,
    coworkerOrder,
    coworkerPagination
  );
  const coworkerQuery = useGetUserCoworkersQuery(query);

  const coworkers = useMemo(() => {
    if (!coworkerQuery.data?.data) {
      return PaginatedResponse.empty<UserCoworker>();
    }
    return PaginatedResponse.from<UserCoworker>(
      UserCoworker.fromJSONArray(coworkerQuery.data.data),
      coworkerQuery.data.headers
    );
  }, [coworkerQuery.data]);

  const renderNameColumn = useCallback((userCoworker: UserCoworker) => {
    if (!userCoworker.name) return <Icon>remove</Icon>;
    return (
      <NameCellContainer>
        <IndividualAvatar
          individual={userCoworker.asIndividual()}
          session={session}
        />
        <RowTitle>{userCoworker.name.fullName}</RowTitle>
      </NameCellContainer>
    );
  }, [session]);

  const renderEmailColumn = useCallback((userCoworker: UserCoworker) => {
    if (!userCoworker.email) return <Icon>remove</Icon>;
    const email = userCoworker.email.toString();
    return <Link href={`mailto:${email}`}>{email}</Link>;
  }, []);

  const columnHelper = createMRTColumnHelper<UserCoworker>();

  const columns = useMemo(() => [
    columnHelper.accessor(renderNameColumn, {
      header: "Name",
      id: "name",
    }),
    columnHelper.accessor(renderEmailColumn, {
      header: "Email",
      id: "email",
    }),
  ], []);

  const table = useMaterialReactTable({
    columns,
    data: coworkers.data,
    enableRowSelection: false,
    enableTableHead: true,
    muiTableBodyRowProps: ({ row }) => {
      const disabled = selectedMemberUserIds?.some((id) => row.original?.userId && id.isEqualTo(row.original.userId));
      return {
        onClick: disabled ? undefined : () => onSelected(row.original),
        sx: {
          pointerEvents: disabled ? "none" : "auto",
          cursor: disabled ? "default" : "pointer",
        },
      };
    },
    enableColumnPinning: true,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiToolbarAlertBannerProps: coworkerQuery.isError
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    rowCount: coworkers.totalElements,
    onPaginationChange: (updater) => {
      if (typeof updater === "function") {
        const { pageIndex: newPageIndex, pageSize: newPageSize } = updater({ pageIndex, pageSize });
        setPageIndex(newPageIndex);
        setPageSize(newPageSize);
      } else {
        setPageIndex(updater.pageIndex);
        setPageSize(updater.pageSize);
      }
    },
    state: {
      isLoading: coworkerQuery.isLoading,
      pagination: { pageIndex, pageSize },
      showAlertBanner: coworkerQuery.isError,
      showProgressBars: coworkerQuery.isFetching,
    },
  });

  return <MaterialReactTable table={table} />;
}
